import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as agencyPaymentMethodActions from '@redux/modules/agencyPaymentMethod'

import defaultFormState, { requiredFields } from '@models/agencyPaymentMethod'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { AgencyPaymentMethodModel, AgencyPaymentMethodRequestOptions, PaymentMethodModel } from '@models/types'

type CreateAgencyPaymentMethodParams = {
  agencyPaymentMethodParams: Partial<AgencyPaymentMethodModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyPaymentMethodRequestOptions,
}

const createAgencyPaymentMethod = (params: CreateAgencyPaymentMethodParams) => {
  const { dispatch, agencyPaymentMethodParams, requestOptions } = params
  const { createAgencyPaymentMethod: createFn } = agencyPaymentMethodActions

  return dispatch(createFn(agencyPaymentMethodParams, requestOptions))
}

type UpdateAgencyPaymentMethodParams = {
  agencyPaymentMethod: AgencyPaymentMethodModel,
  agencyPaymentMethodParams: Partial<AgencyPaymentMethodModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyPaymentMethodRequestOptions,
}

const updateAgencyPaymentMethod = (params: UpdateAgencyPaymentMethodParams) => {
  const {
    dispatch, agencyPaymentMethod, agencyPaymentMethodParams, requestOptions,
  } = params
  const { updateAgencyPaymentMethod: updateFn } = agencyPaymentMethodActions

  const updatedParams = {
    id: agencyPaymentMethod.id,
    ...agencyPaymentMethodParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAgencyPaymentMethodParams = {
  agencyPaymentMethod: DeleteParams<AgencyPaymentMethodModel>,
  dispatch: AppDispatch,
}

const deleteAgencyPaymentMethod = (params: DeleteAgencyPaymentMethodParams) => {
  const { dispatch, agencyPaymentMethod } = params
  const { deleteAgencyPaymentMethod: deleteFn } = agencyPaymentMethodActions

  return dispatch(deleteFn(agencyPaymentMethod))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyPaymentMethodForm(
  agencyPaymentMethod: Partial<AgencyPaymentMethodModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const agencyPaymentMethodForm = useForm(
    defaultFormState,
    { entity: agencyPaymentMethod, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [agencyPaymentMethod.id, agencyPaymentMethod.cache_key],
  )

  return {
    ...agencyPaymentMethodForm,
  }
}

export const useRelations = (agencyPaymentMethod: Partial<AgencyPaymentMethodModel> = {}) => {
  const { agency_id, payment_method_id } = agencyPaymentMethod

  const { agencies, paymentMethods } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}
  // @ts-expect-error TS2322
  const paymentMethod: PaymentMethodModel = payment_method_id && paymentMethods[payment_method_id]
    ? paymentMethods[payment_method_id]
    : {}

  return {
    agency,
    paymentMethod,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AgencyPaymentMethodRequestOptions,
}

function useAgencyPaymentMethod(initEntity: Partial<AgencyPaymentMethodModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: agencyPaymentMethod }: {
    entity: AgencyPaymentMethodModel,
  } = useLatestEntity(initEntity, 'agencyPaymentMethods')
  const { id, fee_type, fee_value } = agencyPaymentMethod

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, paymentMethod } = useRelations(agencyPaymentMethod)

  useReduxAction(
    'agencyPaymentMethods',
    'loadAgencyPaymentMethod',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.agencyPaymentMethods)

  const displayFee = fee_type === 'percentage' ? `${fee_value}%` : `$${fee_value}`

  return {
    agency,
    agencyPaymentMethod,
    callbacks: {
      createAgencyPaymentMethod: (
        agencyPaymentMethodParams: Partial<AgencyPaymentMethodModel>,
        entityOptions?: AgencyPaymentMethodRequestOptions,
      ) => (
        createAgencyPaymentMethod({ agencyPaymentMethodParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAgencyPaymentMethod: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyPaymentMethodModal',
        payload: { agencyPaymentMethod },
      }),
      deleteAgencyPaymentMethod: () => deleteAgencyPaymentMethod({ dispatch, agencyPaymentMethod }),
      updateAgencyPaymentMethod: (
        agencyPaymentMethodParams: Partial<AgencyPaymentMethodModel>,
        entityOptions?: AgencyPaymentMethodRequestOptions,
      ) => (
        updateAgencyPaymentMethod({
          agencyPaymentMethod, agencyPaymentMethodParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    displayFee,
    loading: loading && !loadedIds.includes(agencyPaymentMethod.id),
    paymentMethod,
    updating,
  }
}

export default useAgencyPaymentMethod
